/** Generates a closed sharp polygon path. */
export function generatePath(points: number[][]) {
  const path = points.map(([x, y]) => `${x},${y}`).join(" L");
  return `M${path} Z`;
}

/**
 * Generates a closed polygon path with rounded corners via quadratic Béziers.
 * `radius` is the corner rounding radius (clamped to half the shortest adjacent edge).
 */
export function generateRoundedPath(points: number[][], radius = 8): string {
  const n = points.length;
  if (n < 3) return generatePath(points);

  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));

  const dist = (a: number[], b: number[]) =>
    Math.sqrt((b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2);

  let d = "";

  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const curr = points[i];
    const next = points[(i + 1) % n];

    const dPrev = dist(prev, curr);
    const dNext = dist(curr, next);
    const r = clamp(radius, 0, Math.min(dPrev, dNext) / 2);

    // Unit vectors toward adjacent points
    const ux = (prev[0] - curr[0]) / dPrev;
    const uy = (prev[1] - curr[1]) / dPrev;
    const vx = (next[0] - curr[0]) / dNext;
    const vy = (next[1] - curr[1]) / dNext;

    // Points r-distance along each edge from the corner
    const p1 = [curr[0] + ux * r, curr[1] + uy * r];
    const p2 = [curr[0] + vx * r, curr[1] + vy * r];

    if (i === 0) {
      d += `M${p1[0]},${p1[1]}`;
    } else {
      d += `L${p1[0]},${p1[1]}`;
    }

    // Quadratic Bézier through the corner
    d += ` Q${curr[0]},${curr[1]} ${p2[0]},${p2[1]}`;
  }

  d += " Z";
  return d;
}
